import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";

export interface useUncontrolledStateArgs<T> {
  setState?: (value: T | ((prevState: T) => T)) => void;
  state: T;
}

/**
 * A hook for managing state that can be both controlled and uncontrolled.
 *
 * When `setState` is provided (controlled mode):
 * - Returns the external `state` prop directly
 * - Calls the external `setState` when the setter is invoked
 * - The parent component controls the actual state value
 * - **Optimized behavior**: Direct values are passed as-is to `setState`, while function
 *   updates use the updater pattern for race-condition safety
 *
 * When `setState` is not provided (uncontrolled mode):
 * - Maintains its own internal state initialized from `state`
 * - Updates internal state when the setter is invoked
 *
 * The returned setter function maintains referential equality across renders (using `useCallback`),
 * which prevents unnecessary re-renders in child components that depend on it.
 *
 * **Important Notes:**
 * - Components should NOT switch between controlled and uncontrolled modes dynamically.
 *   Once a component is controlled (setState provided), it should remain controlled, and vice versa.
 * - In uncontrolled mode, subsequent changes to the `state` prop are ignored after initialization.
 *   Only the initial value is used to set the internal state.
 * - Ensure `setState` reference is stable (wrap in `useCallback` if necessary) to maintain
 *   the memoization benefits of the returned setter function.
 * - In controlled mode, the external `setState` receives direct values for simple updates
 *   (e.g., `setState(false)`) and receives the calculated value for function updates
 *   (e.g., `setState(prev => !prev)` is handled internally, and the result is passed).
 *
 * @template T - The type of the state value
 * @param args - Configuration object for the hook
 * @param args.state - The state value (current value if controlled, initial value if uncontrolled)
 * @param args.setState - Optional setter function for controlled mode. Can accept both direct values
 *   and the standard React `SetStateAction<T>` type (value or updater function).
 * @returns A tuple containing the state value and a stable setter function
 *
 * @example
 * ```tsx
 * // Controlled mode: parent manages state
 * function ControlledInput({ value, onChange }: { value: string; onChange: (val: string) => void }) {
 *   const [inputValue, setInputValue] = useUncontrolledState({
 *     state: value,
 *     setState: onChange
 *   });
 *
 *   return <input value={inputValue} onChange={(e) => setInputValue(e.target.value)} />;
 * }
 *
 * const [text, setText] = useState("hello");
 * <ControlledInput value={text} onChange={setText} />
 * ```
 *
 * @example
 * ```tsx
 * // Uncontrolled mode: component manages its own state
 * function UncontrolledInput({ defaultValue }: { defaultValue: string }) {
 *   const [inputValue, setInputValue] = useUncontrolledState({
 *     state: defaultValue,
 *     // No setState provided - uncontrolled mode
 *   });
 *
 *   return <input value={inputValue} onChange={(e) => setInputValue(e.target.value)} />;
 * }
 *
 * <UncontrolledInput defaultValue="hello" />
 * ```
 *
 * @example
 * ```tsx
 * // Supports functional updates in both modes
 * const [count, setCount] = useUncontrolledState({
 *   state: 0,
 *   setState: parentSetCount
 * });
 * setCount(prev => prev + 1); // Functional update works correctly
 * ```
 */
export const useUncontrolledState = <T>({
  state,
  setState,
}: useUncontrolledStateArgs<T>): [T, Dispatch<SetStateAction<T>>] => {
  const [uncontrolledState, setUncontrolledState] = useState(state);
  const isControlledRef = useRef(setState !== undefined);

  // Warn if component switches between controlled and uncontrolled modes
  if (process.env.NODE_ENV !== "production") {
    const wasControlled = isControlledRef.current;
    const isControlled = setState !== undefined;

    if (wasControlled !== isControlled) {
      console.warn(
        "useUncontrolledState: A component is changing from " +
          (wasControlled ? "controlled to uncontrolled" : "uncontrolled to controlled") +
          ". This is not supported and may cause unexpected behavior. " +
          "Components should not switch between controlled and uncontrolled modes during their lifetime."
      );
    }

    isControlledRef.current = isControlled;
  }

  const handleStateChange = useCallback<Dispatch<SetStateAction<T>>>(
    (newVal) => {
      if (setState !== undefined) {
        // Controlled mode: pass through the same type (function or value) to external setState
        if (newVal instanceof Function) {
          // Function update - use updater pattern for race-condition safety
          setState((prev) => {
            const calculatedValue = newVal(prev);
            setUncontrolledState(calculatedValue);
            return calculatedValue;
          });
        } else {
          // Direct value update - pass value directly to external setState
          setState(newVal);
          setUncontrolledState(newVal);
        }
      } else {
        // Uncontrolled mode: update internal state
        setUncontrolledState(newVal);
      }
    },
    [setState]
  );

  return [setState !== undefined ? state : uncontrolledState, handleStateChange];
};
